import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Plus, Edit3, Trash2, ChevronUp, ChevronDown, Loader2, X, AlertCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// ConditionScriptAuditPanel — mini-studio pane for the LEGACY
// `condition_scripts` collection. Mirrors MultiDefectAuditPanel's UX but
// talks to `/api/condition-scripts` and translates the field-name
// difference (`body` in the backend ↔ `comment` in the shared modal).
//
// Wire into any "One Comment per Area" card by passing:
//   area   = { key: inspection_category (e.g. 'Ridging'), label: 'Ridge' }
//   api    = axios-like client
//   currentRoofKey       = canonical roof_key of the property (for defaults)
//   onScriptsChanged     = callback so the parent dropdown can refetch
//   LibraryEditModal     = the shared modal (imported from AreaDefectsAccordion)
// ---------------------------------------------------------------------------
export default function ConditionScriptAuditPanel({
  area, api, currentRoofKey, onScriptsChanged, LibraryEditModal,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterRoof, setFilterRoof] = useState(''); // '' = All roof types
  const [expandedComment, setExpandedComment] = useState({}); // { id: true }
  const [editing, setEditing] = useState(null);   // full row being edited
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ─── Load ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(
        `/condition-scripts?inspection_category=${encodeURIComponent(area.key)}`
      );
      const list = (Array.isArray(r.data) ? r.data : []).slice()
        // Same ordering used in MultiDefectAuditPanel — sort_order asc, name asc.
        .sort((a, b) => (a.sort_order - b.sort_order) || (a.name || '').localeCompare(b.name || ''));
      setRows(list);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [api, area.key]);

  useEffect(() => { load(); }, [load]);

  // Collect roof_key options from what's in the current list so the
  // filter dropdown only shows relevant keys (mirrors Multi Defect UX).
  const roofKeyOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      for (const k of (r.roof_types || [])) s.add(k);
      // Fold legacy single roof_type in too so admin-only rows stay filterable.
      if (r.roof_type) s.add(r.roof_type);
    }
    return Array.from(s).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!filterRoof) return rows;
    if (filterRoof === '__generic__') {
      return rows.filter(r => (r.roof_types || []).length === 0 && !r.roof_type);
    }
    return rows.filter(r =>
      (r.roof_types || []).includes(filterRoof) || r.roof_type === filterRoof
    );
  }, [rows, filterRoof]);

  // Build a full PUT payload from a row — needed because
  // `ConditionScriptInput` has ALL fields defaulted, so a partial PUT
  // would wipe out unset fields. Spread the existing row + apply overrides.
  const rowToPutPayload = (r, overrides = {}) => ({
    category:            r.category || area.key,
    inspection_category: r.inspection_category || area.key,
    name:                r.name || '',
    body:                r.body || r.body_text || '',
    roof_type:           r.roof_type || '',
    roof_types:          Array.isArray(r.roof_types) ? r.roof_types : [],
    recommendation:      r.recommendation || '',
    sort_order:          Number(r.sort_order) || 0,
    active:              r.active !== false,
    ...overrides,
  });

  // ─── Row actions ──────────────────────────────────────────────────────
  const move = async (index, direction) => {
    const src = visibleRows[index];
    const tgt = visibleRows[index + direction];
    if (!src || !tgt) return;
    setBusy(true);
    try {
      const a = Number(src.sort_order) || 0;
      const b = Number(tgt.sort_order) || 0;
      const nextA = a === b ? index + direction : b;
      const nextB = a === b ? index : a;
      await Promise.all([
        api.put(`/condition-scripts/${src._id}`, rowToPutPayload(src, { sort_order: nextA })),
        api.put(`/condition-scripts/${tgt._id}`, rowToPutPayload(tgt, { sort_order: nextB })),
      ]);
      await load();
      onScriptsChanged?.();
    } catch (e) {
      alert('Reorder failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      // Legacy endpoint hard-deletes; existing appraisal narratives that
      // already inlined the body text keep it (they store the paragraph
      // in `{area}_condition`, not a foreign-key reference).
      await api.delete(`/condition-scripts/${confirmDelete._id}`);
      await load();
      onScriptsChanged?.();
      setConfirmDelete(null);
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveAdd = async (payload) => {
    setBusy(true);
    try {
      const explicitRoofTypes = Array.isArray(payload.roof_types) ? payload.roof_types : null;
      const fallback = filterRoof && filterRoof !== '__generic__' ? [filterRoof]
                     : currentRoofKey ? [currentRoofKey]
                     : [];
      await api.post('/condition-scripts', {
        category:            area.key,
        inspection_category: area.key,
        name:                payload.name,
        body:                payload.comment, // modal emits `comment`, backend uses `body`
        recommendation:      payload.recommendation,
        roof_types:          explicitRoofTypes !== null ? explicitRoofTypes : fallback,
        roof_type:           '',
        sort_order:          0,
        active:              true,
      });
      await load();
      onScriptsChanged?.();
      setAdding(false);
    } catch (e) {
      alert('Add failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (payload) => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.put(`/condition-scripts/${editing._id}`, rowToPutPayload(editing, {
        name:           payload.name,
        body:           payload.comment,
        recommendation: payload.recommendation,
        roof_types:     Array.isArray(payload.roof_types) ? payload.roof_types : [],
      }));
      await load();
      onScriptsChanged?.();
      setEditing(null);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div
      data-testid={`condition-audit-panel-${area.key}`}
      className="rounded-lg border border-emerald-300 bg-emerald-50/30 overflow-hidden flex flex-col"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-emerald-100 border-b border-emerald-300">
        <div>
          <h4 className="text-xs font-bold text-emerald-900 uppercase tracking-wider">
            📚 {area.label} script library
          </h4>
          <p className="text-[10px] text-emerald-700/80 mt-0.5">
            {rows.length} entries · sort with ↑↓ · click ✎ to edit
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={filterRoof}
            onChange={(e) => setFilterRoof(e.target.value)}
            className="text-[10px] px-1.5 py-1 border border-emerald-300 rounded bg-white"
            data-testid={`condition-audit-filter-${area.key}`}
          >
            <option value="">All roof types</option>
            <option value="__generic__">Generic only</option>
            {roofKeyOptions.map(k => (
              <option key={k} value={k}>{String(k).replace(/_/g, ' ')}</option>
            ))}
          </select>
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold bg-emerald-700 text-white rounded hover:bg-emerald-800"
            data-testid={`condition-audit-add-${area.key}`}
          >
            <Plus size={10} /> New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="animate-spin" size={12} /> Loading library…
        </div>
      ) : error ? (
        <div className="p-3 text-xs text-red-700 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      ) : visibleRows.length === 0 ? (
        <p className="p-4 text-xs text-slate-400 italic">No entries match this filter.</p>
      ) : (
        <div className="divide-y divide-emerald-100 max-h-[560px] overflow-y-auto">
          {visibleRows.map((r, i) => {
            const isExpanded = !!expandedComment[r._id];
            const rowRoofBadges = [
              ...(Array.isArray(r.roof_types) ? r.roof_types : []),
              // Surface the legacy single roof_type too so admin-added rows
              // stay visible even before they get migrated to roof_types[].
              ...(r.roof_type ? [r.roof_type] : []),
            ];
            const badgeText = rowRoofBadges.length === 0 ? 'generic' : rowRoofBadges.join(', ');
            const bodyText = r.body || r.body_text || '';
            return (
              <div
                key={r._id}
                className="px-2.5 py-2 hover:bg-emerald-50/60 transition-colors"
                data-testid={`condition-audit-row-${r._id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex flex-col shrink-0 gap-0.5">
                    <button
                      onClick={() => move(i, -1)}
                      disabled={busy || i === 0}
                      className="text-slate-400 hover:text-emerald-800 disabled:opacity-20"
                      title="Move up"
                      data-testid={`condition-audit-row-up-${r._id}`}
                    ><ChevronUp size={12} /></button>
                    <button
                      onClick={() => move(i, +1)}
                      disabled={busy || i === visibleRows.length - 1}
                      className="text-slate-400 hover:text-emerald-800 disabled:opacity-20"
                      title="Move down"
                      data-testid={`condition-audit-row-down-${r._id}`}
                    ><ChevronDown size={12} /></button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-bold text-slate-900 truncate" title={r.name}>
                        {r.name}
                      </span>
                      <span className="text-[9px] font-mono text-emerald-700/70 truncate">
                        {badgeText}
                      </span>
                    </div>
                    <p
                      title={bodyText || '(no comment)'}
                      onClick={() => setExpandedComment(prev => ({ ...prev, [r._id]: !prev[r._id] }))}
                      className={`text-[10px] text-slate-700 mt-0.5 leading-snug cursor-pointer ${isExpanded ? '' : 'line-clamp-2'}`}
                      data-testid={`condition-audit-row-comment-${r._id}`}
                    >
                      {bodyText || <span className="italic text-slate-400">(no comment)</span>}
                      {!isExpanded && bodyText && bodyText.length > 100 && (
                        <span className="text-emerald-700 font-semibold ml-1">…</span>
                      )}
                    </p>
                    {r.recommendation && (
                      <p
                        className="text-[10px] text-emerald-800 mt-0.5 leading-snug"
                        data-testid={`condition-audit-row-rec-${r._id}`}
                      >
                        <span className="font-semibold">Rec:</span> {r.recommendation}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      onClick={() => setEditing(r)}
                      className="p-1 text-slate-500 hover:text-emerald-800 hover:bg-emerald-100 rounded"
                      title="Edit"
                      data-testid={`condition-audit-row-edit-${r._id}`}
                    ><Edit3 size={11} /></button>
                    <button
                      onClick={() => setConfirmDelete(r)}
                      className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                      data-testid={`condition-audit-row-delete-${r._id}`}
                    ><Trash2 size={11} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <LibraryEditModal
          title={`Add ${area.label} defect to library`}
          initial={{
            name: '',
            comment: '',
            recommendation: '',
            roof_types: filterRoof && filterRoof !== '__generic__' ? [filterRoof]
                       : currentRoofKey ? [currentRoofKey]
                       : [],
          }}
          busy={busy}
          onSave={saveAdd}
          onCancel={() => setAdding(false)}
        />
      )}
      {editing && (
        <LibraryEditModal
          title={`Edit ${area.label} defect · ${editing.name}`}
          initial={{
            name: editing.name || '',
            // Translate legacy `body`/`body_text` back into the modal's
            // `comment` field.
            comment: editing.body || editing.body_text || '',
            recommendation: editing.recommendation || '',
            roof_types: Array.isArray(editing.roof_types) ? editing.roof_types : [],
          }}
          busy={busy}
          onSave={saveEdit}
          onCancel={() => setEditing(null)}
        />
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-slate-900">Delete script from library?</h4>
              <button onClick={() => setConfirmDelete(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <p className="text-xs text-slate-600 mb-4">
              <b>{confirmDelete.name}</b> will be removed from the {area.label} dropdown.
              Existing appraisals that already inlined this comment keep their text
              (comments are copied into the appraisal, not referenced).
            </p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
              <button
                onClick={doDelete}
                disabled={busy}
                data-testid="condition-audit-delete-confirm"
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
              >
                <Trash2 size={12} /> {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
